import {
    type FC
} from "react";
import {
    View
} from "react-native";
import type ISidebarMenuBadgeProps from "./type";
import stylesheet, {
    SIDEBAR_BADGE_TITLE_STYLE,
    SIDEBAR_BADGE_VARIANT,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useSidebarSubItemContext,
    useSidebarRootContext,
    useSidebarItemContext
} from "../../context";
import {
    SIDEBAR_SUB_LABEL_VARIANT,
    getSidebarMeasures
} from "../../stylesheet";
import {
    STATE_COLOR_KEYS
} from "../../../../helpers/theme/state";
import {
    getSidebarBadgeText
} from "../../util";
import Badge from "../../../badge";
import Text from "../../../text";

const SidebarMenuBadge: FC<ISidebarMenuBadgeProps> = ({
    customLocalize: customLocalizeProp,
    customTheme: customThemeProp,
    type = "neutral",
    max = 99,
    count,
    label,
    style,
    ...props
}) => {
    const root = useSidebarRootContext({
        part: "Sidebar.MenuBadge"
    });

    const item = useSidebarItemContext();

    const subItem = useSidebarSubItemContext();

    const customLocalize = customLocalizeProp ?? root?.customLocalize;

    const customTheme = customThemeProp ?? root?.customTheme;

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const measures = getSidebarMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        spaces
    });

    const isCollapsed = root?.isCollapsed ?? false;

    const {
        overlay: overlayDynamicStyle,
        corner: cornerDynamicStyle,
        cornerTitleColor,
        isOverlay
    } = useStyles({
        isActionVisible: item?.isActionVisible ?? false,
        hasAction: item?.hasAction ?? false,
        variant: root?.variant ?? "sidebar",
        size: item?.size ?? "medium",
        isCollapsed,
        typography,
        iconSizes,
        measures,
        radiuses,
        borders,
        colors,
        spaces,
        type
    });

    const text = getSidebarBadgeText({
        locale: activeLocale,
        count,
        max
    });

    if(subItem) {
        return <View
            {...props}
            style={[
                style,
                stylesheet.plain
            ]}
        >
            <Text
                aria-label={label ? `${text} ${label}` : undefined}
                customColor={colors.content.text.mid}
                variant={SIDEBAR_SUB_LABEL_VARIANT}
                style={SIDEBAR_BADGE_TITLE_STYLE}
                customTheme={customTheme}
            >
                {text}
            </Text>
        </View>;
    }

    if(isCollapsed) {
        return <View
            {...props}
            style={[
                style,
                stylesheet.corner,
                cornerDynamicStyle
            ]}
            aria-hidden={true}
        >
            <Text
                style={SIDEBAR_BADGE_TITLE_STYLE}
                variant={SIDEBAR_BADGE_VARIANT}
                customColor={cornerTitleColor}
                customTheme={customTheme}
                numberOfLines={1}
            >
                {text}
            </Text>
        </View>;
    }

    const badge = <Badge
        customColor={item?.isActive ? colors.system.high[STATE_COLOR_KEYS[type]] : undefined}
        aria-label={label ? `${text} ${label}` : undefined}
        titleStyle={SIDEBAR_BADGE_TITLE_STYLE}
        style={stylesheet.container}
        customTheme={customTheme}
        variant="tonal"
        size="small"
        title={text}
        type={type}
    />;

    if(!isOverlay) {
        return <View
            {...props}
            style={[
                style,
                stylesheet.container,
                stylesheet.inert
            ]}
        >
            {badge}
        </View>;
    }

    return <View
        {...props}
        style={[
            style,
            stylesheet.overlay,
            overlayDynamicStyle
        ]}
    >
        {badge}
    </View>;
};
export default SidebarMenuBadge;
